import {
  Box,
  Button,
  Stack,
  Table,
  Tabs,
  Text,
  VStack,
  Card,
  Badge,
  Flex,
  Input
} from '@chakra-ui/react';
import {
  File,
  Plus,
  UploadSimple,
  X,
  CheckCircle,
  XCircle,
  ArrowClockwise,
  Trash,
  MagnifyingGlass,
  Users
} from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import {
  apiCreateJobCandidate,
  apiCreateJobCandidateFromVeteransResume,
  apiAddCandidateToJob,
  apiGetExtractorProviders,
  apiGetAgencyCustomFeatures
} from '@/api/services';
import {
  apiGetJobAvailableCandidates,
  GetJobAvailableCandidatesResponse
} from '@/api/services/job.service';
import { CandidateWithJobContext, Candidate } from '@/types/Candidate';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { AgencyCustomFeature } from '@/types/AgencyCustomFeature';
import { PromptKey } from '@/types/enums';
import {
  AgencyFeature,
  LlmModelProvider,
  getLlmModelProviderSupportFileTypes
} from '@/types/enums';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { getCandidateFullName, getCandidateLatestWorkExperience } from '@/utils/candidate';
import { getFileSize } from '@/utils';
import { LoadingSkeleton } from '@/components/layout/LoadingSkeleton';
import { MAX_RESUME_FILE_SIZE } from '@/config/constants';

// Constants
const MAX_CONCURRENT_UPLOADS = 3;
const FILE_SIZE_LIMIT = MAX_RESUME_FILE_SIZE;
const MAX_FILES = 20;

// Types
type FileStatus = 'pending' | 'submitting' | 'success' | 'error';

interface FileWithStatus {
  file: File;
  status: FileStatus;
  error?: string;
}

interface Provider {
  analysis_id: string | null;
  analysis_name: PromptKey;
  provider: LlmModelProvider | null;
  loading: boolean;
  error: string | null;
}

interface AddNewCandidateProps {
  jobId: string;
  filled?: boolean;
  onAddCandidate: () => void;
  disabled?: boolean;
}

// Utility functions
const getStatusColor = (status: FileStatus): string => {
  const statusColors = {
    success: '#38A169',
    error: '#E53E3E',
    submitting: '#3182CE',
    pending: '#718096'
  };
  return statusColors[status];
};

const validateFile = (file: File, supportedTypes: string[]): string | null => {
  const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

  if (!supportedTypes.includes(fileExtension)) {
    return `File type ${fileExtension} is not supported. Please upload ${supportedTypes.join(
      ', '
    )} files`;
  }

  if (file.size > FILE_SIZE_LIMIT) {
    return `File size exceeds ${getFileSize(FILE_SIZE_LIMIT)}`;
  }

  return null;
};

type UploadMode = 'standard' | 'veterans';

export default function AddNewCandidate({
  jobId,
  onAddCandidate,
  filled = false,
  disabled = false
}: AddNewCandidateProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'upload' | 'select'>('upload');
  const [uploadMode, setUploadMode] = useState<UploadMode>('standard');
  const [hasVeteransResumeFeature, setHasVeteransResumeFeature] = useState(false);

  // Provider state
  const [provider, setProvider] = useState<Provider>({
    analysis_id: null,
    analysis_name: PromptKey.JDExtractor,
    provider: null,
    loading: true,
    error: null
  });

  // Upload tab state
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Select tab state
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [addingCandidate, setAddingCandidate] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCandidates, setTotalCandidates] = useState(0);

  // Computed values
  const pendingFiles = useMemo(() => files.filter((f) => f.status === 'pending'), [files]);
  const submittingFiles = useMemo(() => files.filter((f) => f.status === 'submitting'), [files]);
  const errorFiles = useMemo(() => files.filter((f) => f.status === 'error'), [files]);
  const successFiles = useMemo(() => files.filter((f) => f.status === 'success'), [files]);

  const hasPendingOrSubmitting = pendingFiles.length > 0 || submittingFiles.length > 0;
  const canUpload = files.length > 0 && !hasPendingOrSubmitting;
  const canAddMore = files.length < MAX_FILES;

  // Get supported file types based on provider
  const supportedFileTypes = useMemo(
    () =>
      provider?.provider
        ? getLlmModelProviderSupportFileTypes(provider.provider)
        : ['.pdf', '.txt'],
    [provider?.provider]
  );

  // Provider fetch function
  const fetchProviders = useCallback(async () => {
    const defaultProvider: Provider = {
      analysis_id: null,
      analysis_name: PromptKey.JDExtractor,
      provider: null,
      loading: false,
      error: null
    };

    setProvider({ ...defaultProvider, loading: true, error: null });

    try {
      const response = await fetchAPI<AnalysisProvider[]>({
        request: apiGetExtractorProviders
      });

      if (response.success) {
        const providersData = response.data;
        const extractorProvider = providersData.find(
          (p) => p.analysis_name === PromptKey.JDExtractor
        );

        if (extractorProvider) {
          setProvider({
            ...extractorProvider,
            loading: false,
            error: null
          });
        } else {
          console.error('No extractor provider configured.');
          setProvider({
            ...defaultProvider,
            error: 'No extractor provider configured. Please contact support to enable file upload.'
          });
        }
      } else {
        console.error('Error fetching analysis providers:', response.message);
        setProvider({
          ...defaultProvider,
          error: 'Failed to load file upload options. Please try again later.'
        });
      }
    } catch (error) {
      console.error('Unexpected error fetching analysis providers:', error);
      setProvider({
        ...defaultProvider,
        error: 'Failed to load file upload options. Please try again later.'
      });
    }
  }, []);

  const retryProviderFetch = useCallback(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Candidate loading function with pagination support
  const loadAvailableCandidates = useCallback(
    async (page: number, keyword: string, append = false) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingCandidates(true);
      }

      try {
        const response = await fetchAPI<GetJobAvailableCandidatesResponse>({
          request: () =>
            apiGetJobAvailableCandidates({
              jobId,
              params: {
                page,
                limit: 30,
                keyword: keyword.trim()
              }
            })
        });

        if (response.success) {
          if (append) {
            setCandidates((prevCandidates) => [...prevCandidates, ...response.data.candidates]);
          } else {
            setCandidates(response.data.candidates);
          }
          setCurrentPage(response.data.current_page);
          setHasNextPage(response.data.current_page < response.data.total_pages);
          setTotalCandidates(response.data.total_count);
        }
      } catch (error) {
        console.error('Failed to load available candidates:', error);
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoadingCandidates(false);
        }
      }
    },
    [jobId]
  );

  // Fetch providers on mount
  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFiles([]);
      setFileError(null);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setCandidates([]);
      setCurrentPage(1);
      setHasNextPage(false);
      setLoadingMore(false);
      setTotalCandidates(0);
      setUploadMode('standard');
      fetchAPI<AgencyCustomFeature[]>({ request: apiGetAgencyCustomFeatures }).then((res) => {
        if (res.success) {
          setHasVeteransResumeFeature(
            res.data.some((f) => f.feature_key === AgencyFeature.VeteransResume && f.is_enabled)
          );
        }
      });
    }
  }, [open]);

  // Load candidates when selecting the "select" tab
  useEffect(() => {
    if (open && activeTab === 'select') {
      loadAvailableCandidates(1, '');
    }
  }, [activeTab, open, loadAvailableCandidates]);

  // Handle debounced search query changes
  useEffect(() => {
    if (open && activeTab === 'select') {
      setCurrentPage(1);
      loadAvailableCandidates(1, debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, open, activeTab, loadAvailableCandidates]);

  // Scroll handler for infinite scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      // If we're near the bottom (within 50px) and there's a next page and not already loading
      if (scrollBottom < 50 && hasNextPage && !loadingMore && !loadingCandidates) {
        loadAvailableCandidates(currentPage + 1, debouncedSearchQuery, true);
      }
    },
    [
      hasNextPage,
      loadingMore,
      loadingCandidates,
      currentPage,
      debouncedSearchQuery,
      loadAvailableCandidates
    ]
  );

  // File submission logic
  const submitFile = useCallback(
    async (fileWithStatus: FileWithStatus, index: number) => {
      setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, status: 'submitting' } : f)));

      try {
        const apiCall =
          uploadMode === 'veterans'
            ? () =>
                apiCreateJobCandidateFromVeteransResume({
                  jobId,
                  payload: { resume: fileWithStatus.file }
                })
            : () =>
                apiCreateJobCandidate({
                  jobId,
                  payload: { resume: fileWithStatus.file }
                });

        const candidateResponse = await fetchAPI<CandidateWithJobContext>({ request: apiCall });

        if (candidateResponse.success) {
          setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, status: 'success' } : f)));
        } else {
          setFiles((prev) =>
            prev.map((f, i) =>
              i === index ? { ...f, status: 'error', error: candidateResponse.message } : f
            )
          );
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setFiles((prev) =>
          prev.map((f, i) =>
            i === index ? { ...f, status: 'error', error: 'Failed to upload file' } : f
          )
        );
      }
    },
    [jobId, uploadMode]
  );

  // Batch upload processing
  const onSubmit = useCallback(async () => {
    if (files.length === 0) {
      setFileError('At least one file is required');
      return;
    }

    setIsSubmitting(true);
    setFileError(null);

    try {
      const pendingIndices = pendingFiles.map((file) =>
        files.findIndex((f) => f.file === file.file)
      );

      // Process files in batches to limit concurrent uploads
      for (let i = 0; i < pendingFiles.length; i += MAX_CONCURRENT_UPLOADS) {
        const batch = pendingFiles.slice(i, i + MAX_CONCURRENT_UPLOADS);
        const batchIndices = pendingIndices.slice(i, i + MAX_CONCURRENT_UPLOADS);
        await Promise.all(batch.map((file, j) => submitFile(file, batchIndices[j])));
      }
    } catch (error) {
      console.error('Error submitting files:', error);
      setFileError('An unexpected error occurred while uploading files. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [files, pendingFiles, submitFile]);

  // Candidate selection function
  const handleAddExistingCandidate = useCallback(
    async (candidateId: string) => {
      setAddingCandidate(candidateId);
      try {
        const response = await fetchAPI<void>({
          request: () =>
            apiAddCandidateToJob({
              jobId,
              payload: { candidate_id: candidateId }
            })
        });

        if (response.success) {
          setOpen(false);
          onAddCandidate();
        }
      } catch (error) {
        console.error('Failed to add candidate to job:', error);
      } finally {
        setAddingCandidate(null);
      }
    },
    [jobId, onAddCandidate]
  );

  // File handling
  const handleFileUpload = useCallback(
    (uploadedFiles: File[]) => {
      const errors: string[] = [];

      // Check if provider is available
      if (provider?.error || provider?.loading) {
        setFileError('File upload is not available. Please wait or try again later.');
        return;
      }

      if (files.length + uploadedFiles.length > MAX_FILES) {
        setFileError(`Maximum ${MAX_FILES} files allowed`);
        return;
      }

      const existingFileNames = new Set(files.map((f) => f.file.name));
      const uniqueNewFiles = uploadedFiles.filter((file) => !existingFileNames.has(file.name));

      if (uniqueNewFiles.length < uploadedFiles.length) {
        const duplicateFiles = uploadedFiles.filter((file) => existingFileNames.has(file.name));
        errors.push(`Skipped duplicate files: ${duplicateFiles.map((f) => f.name).join(', ')}`);
      }

      uniqueNewFiles.forEach((file) => {
        const error = validateFile(file, supportedFileTypes);
        if (error) errors.push(`${file.name}: ${error}`);
      });

      if (errors.length > 0) {
        setFileError(errors.join('\n'));
        return;
      }

      setFileError(null);
      setFiles((prev) => [
        ...prev,
        ...uniqueNewFiles.map((file) => ({
          file,
          status: 'pending' as FileStatus
        }))
      ]);
    },
    [files, provider, supportedFileTypes]
  );

  const onUploadFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const uploadedFiles = Array.from(e.target.files || []);
      handleFileUpload(uploadedFiles);
      // Reset input value to allow re-uploading the same file
      e.target.value = '';
    },
    [handleFileUpload]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  }, []);

  const handleRetryFile = useCallback(
    (fileWithStatus: FileWithStatus, index: number) => {
      submitFile(fileWithStatus, index);
    },
    [submitFile]
  );

  // Dialog handling
  const handleDialogClose = useCallback(() => {
    setFiles([]);
    setFileError(null);
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setCandidates([]);
    setCurrentPage(1);
    setHasNextPage(false);
    setLoadingMore(false);
    setTotalCandidates(0);
    setActiveTab('upload');
    if (successFiles.length > 0) {
      onAddCandidate();
    }
  }, [successFiles.length, onAddCandidate]);

  const handleOpenChange = useCallback((e: { open: boolean }) => {
    setOpen(e.open);
  }, []);

  // Render helpers
  const renderFileStatus = (status: FileStatus) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={20} color={getStatusColor(status)} />;
      case 'error':
        return <XCircle size={20} color={getStatusColor(status)} />;
      case 'submitting':
        return <Box>Uploading...</Box>;
      case 'pending':
        return <Box>Pending</Box>;
      default:
        return null;
    }
  };

  const renderFileActions = (fileWithStatus: FileWithStatus, index: number) => (
    <Box display="flex" alignItems="center" justifyContent="center" gap="2">
      {fileWithStatus.status === 'error' && (
        <Button
          variant="ghost"
          size="xs"
          borderRadius="full"
          p="0"
          m="0"
          onClick={() => handleRetryFile(fileWithStatus, index)}
          color="primary.900"
          title="Retry upload"
        >
          <ArrowClockwise size={16} />
        </Button>
      )}
      {(fileWithStatus.status === 'pending' || fileWithStatus.status === 'error') && (
        <Button
          variant="ghost"
          size="xs"
          borderRadius="full"
          p="0"
          m="0"
          onClick={() => handleRemoveFile(index)}
          color="neutrals.900"
          title="Remove file"
        >
          <Trash size={16} />
        </Button>
      )}
    </Box>
  );

  return (
    <DialogRoot
      size="xl"
      open={open}
      onOpenChange={handleOpenChange}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
    >
      <DialogTrigger asChild>
        {filled ? (
          <Button
            w="240px"
            h="48px"
            borderRadius="8px"
            bg="primary.900"
            fontSize="16px"
            fontWeight="regular"
            disabled={disabled}
            _hover={{ background: 'primary.700' }}
          >
            <Plus size={24} />
            Add New Candidate
          </Button>
        ) : (
          <Button
            borderColor="primary.900"
            background="background.secondary"
            color="primary.900"
            borderRadius="8px"
            ml="16px"
            fontSize="16px"
            disabled={disabled}
            _hover={{ background: 'primary.900', color: 'neutrals.0' }}
          >
            <Plus size={24} />
            Add Candidate
          </Button>
        )}
      </DialogTrigger>

      <DialogContent
        borderRadius="12px"
        minH="700px"
        h="700px"
        display="flex"
        flexDirection="column"
      >
        <DialogHeader flexShrink={0}>
          <DialogTitle>Add Candidates to Job</DialogTitle>
        </DialogHeader>

        <DialogBody overflow="hidden" display="flex" flexDirection="column" flex="1" minH="0">
          <Tabs.Root
            value={activeTab}
            onValueChange={(e) => setActiveTab(e.value as 'upload' | 'select')}
            colorPalette="blue"
            display="flex"
            flexDirection="column"
            flex="1"
            minH="0"
          >
            <Tabs.List mb="4" flexShrink={0}>
              <Tabs.Trigger value="upload" asChild>
                <Button
                  variant="ghost"
                  fontSize="16px"
                  fontWeight={activeTab === 'upload' ? 'bold' : 'normal'}
                  color={activeTab === 'upload' ? 'primary.900' : 'neutrals.700'}
                  _hover={{ bg: 'neutrals.50' }}
                >
                  <UploadSimple size={20} />
                  Upload Resumes
                </Button>
              </Tabs.Trigger>
              <Tabs.Trigger value="select" asChild>
                <Button
                  variant="ghost"
                  fontSize="16px"
                  fontWeight={activeTab === 'select' ? 'bold' : 'normal'}
                  color={activeTab === 'select' ? 'primary.900' : 'neutrals.700'}
                  _hover={{ bg: 'neutrals.50' }}
                >
                  <Users size={20} />
                  Select Existing
                </Button>
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="upload" overflow="auto" flex="1" minH="0">
              <Stack gap="4">
                {hasVeteransResumeFeature && (
                  <Box display="flex" gap="2">
                    <Button
                      size="sm"
                      borderRadius="8px"
                      fontSize="14px"
                      variant={uploadMode === 'standard' ? 'solid' : 'outline'}
                      background={
                        uploadMode === 'standard' ? 'primary.900' : 'background.secondary'
                      }
                      color={uploadMode === 'standard' ? 'neutrals.0' : 'primary.900'}
                      borderColor="primary.900"
                      onClick={() => {
                        setUploadMode('standard');
                        setFiles([]);
                        setFileError(null);
                      }}
                      disabled={isSubmitting}
                    >
                      Standard Resume
                    </Button>
                    <Button
                      size="sm"
                      borderRadius="8px"
                      fontSize="14px"
                      variant={uploadMode === 'veterans' ? 'solid' : 'outline'}
                      background={
                        uploadMode === 'veterans' ? 'primary.900' : 'background.secondary'
                      }
                      color={uploadMode === 'veterans' ? 'neutrals.0' : 'primary.900'}
                      borderColor="primary.900"
                      onClick={() => {
                        setUploadMode('veterans');
                        setFiles([]);
                        setFileError(null);
                      }}
                      disabled={isSubmitting}
                    >
                      Veterans Resume
                    </Button>
                  </Box>
                )}

                <Box>
                  <Box color="neutrals.800" fontSize="18px" mb="4px">
                    <Box as="span" color="error.500">
                      *
                    </Box>
                    Upload up to {MAX_FILES} resumes or CVs, and we'll automatically extract
                    candidate information.
                  </Box>
                  <Box color="neutrals.500" fontSize="16px">
                    Supported {supportedFileTypes.length === 1 ? 'format' : 'formats'}:{' '}
                    {supportedFileTypes.join(', ')} ({getFileSize(FILE_SIZE_LIMIT)} max per file)
                  </Box>
                </Box>

                {provider.error ? (
                  <Box
                    w="100%"
                    border="2px solid"
                    borderRadius="12px"
                    borderColor="error.300"
                    bg="error.50"
                    p="24px"
                    textAlign="center"
                  >
                    <Text fontSize="18px" fontWeight="bold" color="error.700" mb="8px">
                      Upload Unavailable
                    </Text>
                    <Text fontSize="16px" color="error.600" mb="24px">
                      {provider.error}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={retryProviderFetch}
                      color="neutrals.700"
                      fontSize="16px"
                      borderColor="neutrals.500"
                      borderRadius="8px"
                      p="8px 24px"
                    >
                      Retry
                    </Button>
                  </Box>
                ) : provider.loading ? (
                  <Stack w="100%" p="24px" textAlign="center" gap="8px">
                    <Text fontSize="18px" fontWeight="bold" color="neutrals.700">
                      Loading...
                    </Text>
                    <Text fontSize="16px" color="neutrals.600" mb="12px">
                      Please wait while we load file upload capabilities.
                    </Text>
                    <LoadingSkeleton count={1} />
                  </Stack>
                ) : files.length > 0 ? (
                  <Stack gap="4">
                    <Box width="100%" overflowX="auto">
                      <Table.Root>
                        <Table.Header>
                          <Table.Row bg="neutrals.50" h="48px">
                            <Table.ColumnHeader
                              fontSize="14px"
                              fontWeight="bold"
                              borderColor="neutrals.300"
                              width="50%"
                            >
                              File Name
                            </Table.ColumnHeader>
                            <Table.ColumnHeader
                              fontSize="14px"
                              fontWeight="bold"
                              borderColor="neutrals.300"
                              width="15%"
                            >
                              <Box display="flex" alignItems="center" justifyContent="center">
                                Size
                              </Box>
                            </Table.ColumnHeader>
                            <Table.ColumnHeader
                              fontSize="14px"
                              fontWeight="bold"
                              borderColor="neutrals.300"
                              width="20%"
                            >
                              <Box display="flex" alignItems="center" justifyContent="center">
                                Status
                              </Box>
                            </Table.ColumnHeader>
                            <Table.ColumnHeader
                              fontSize="14px"
                              fontWeight="bold"
                              borderColor="neutrals.300"
                              width="15%"
                            >
                              <Box display="flex" alignItems="center" justifyContent="center">
                                Action
                              </Box>
                            </Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {files.map((fileWithStatus, index) => (
                            <Table.Row key={`${fileWithStatus.file.name}-${index}`}>
                              <Table.Cell width="50%">
                                <Box display="flex" alignItems="center">
                                  <File size={24} color="#4e76ff" />
                                  <Box
                                    ml="12px"
                                    maxW="300px"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    whiteSpace="nowrap"
                                    title={fileWithStatus.file.name}
                                  >
                                    {fileWithStatus.file.name}
                                  </Box>
                                </Box>
                              </Table.Cell>
                              <Table.Cell width="15%">
                                <Box display="flex" alignItems="center" justifyContent="center">
                                  {getFileSize(fileWithStatus.file.size)}
                                </Box>
                              </Table.Cell>
                              <Table.Cell width="20%">
                                <Box display="flex" alignItems="center" justifyContent="center">
                                  {renderFileStatus(fileWithStatus.status)}
                                </Box>
                              </Table.Cell>
                              <Table.Cell width="15%">
                                {renderFileActions(fileWithStatus, index)}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    </Box>

                    <Box>
                      <input
                        id="additionalFiles"
                        type="file"
                        accept={supportedFileTypes.join(',')}
                        multiple
                        style={{ display: 'none' }}
                        onChange={onUploadFile}
                        disabled={!!(provider?.error || provider?.loading)}
                      />
                      <Button
                        variant="outline"
                        borderRadius="8px"
                        border="1px solid"
                        borderColor="neutrals.300"
                        color="neutrals.700"
                        fontSize="14px"
                        onClick={() => document.getElementById('additionalFiles')?.click()}
                        disabled={!canAddMore || !!(provider?.error || provider?.loading)}
                      >
                        <Plus size={26} />
                        Add More Files ({files.length}/{MAX_FILES})
                      </Button>
                    </Box>

                    {fileError && (
                      <Box
                        color="error.700"
                        fontSize="14px"
                        whiteSpace="pre-line"
                        p="12px"
                        bg="error.50"
                        borderRadius="8px"
                      >
                        {fileError}
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Stack>
                    <Box
                      w="100%"
                      h="200px"
                      border="1px dashed"
                      borderColor={fileError ? 'error.700' : 'neutrals.300'}
                      borderRadius="12px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor={provider?.error || provider?.loading ? 'not-allowed' : 'pointer'}
                      bg="neutrals.50"
                      position="relative"
                      _hover={{
                        bg: provider?.error || provider?.loading ? 'neutrals.50' : 'neutrals.100'
                      }}
                      transition="background-color 0.2s"
                      opacity={provider?.error || provider?.loading ? 0.6 : 1}
                    >
                      <input
                        id="resumeFile"
                        type="file"
                        accept={supportedFileTypes.join(',')}
                        multiple
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: provider?.error || provider?.loading ? 'not-allowed' : 'pointer'
                        }}
                        onChange={onUploadFile}
                        disabled={!!(provider?.error || provider?.loading)}
                      />
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        fontSize="14px"
                      >
                        <UploadSimple size={60} color="#404040" />
                        <Box textAlign="center" color="neutrals.900">
                          Drop files here or{' '}
                          <Box as="span" color="primary.900" fontWeight="medium">
                            click to upload
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {fileError && (
                      <Box
                        fontSize="14px"
                        color="error.700"
                        whiteSpace="pre-line"
                        p="12px"
                        bg="error.50"
                        borderRadius="8px"
                      >
                        {fileError}
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </Tabs.Content>

            <Tabs.Content value="select" overflow="auto" flex="1" minH="0">
              <VStack gap="20px" align="stretch">
                <Text color="neutrals.800" fontSize="18px">
                  Select existing candidates from your database to add to this job.
                </Text>

                <Field>
                  <Box position="relative" w="100%">
                    <Input
                      placeholder="Search by name or email"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      paddingLeft="40px"
                      size="lg"
                    />
                    <Box position="absolute" left="12px" top="50%" transform="translateY(-50%)">
                      <MagnifyingGlass size={20} color="#6B7280" />
                    </Box>
                  </Box>
                </Field>

                <Box>
                  <Text fontSize="16px" fontWeight="semibold" color="neutrals.800" mb="16px">
                    Available Candidates ({totalCandidates})
                  </Text>

                  {loadingCandidates ? (
                    <VStack gap="12px">
                      {[1, 2].map((i) => (
                        <Card.Root key={i} p="20px" w="100%">
                          <Flex gap="16px" w="100%">
                            <Skeleton flex="3" h="20px" />
                            <Skeleton flex="1" h="36px" />
                          </Flex>
                          <SkeletonText noOfLines={2} mt="12px" />
                        </Card.Root>
                      ))}
                    </VStack>
                  ) : (
                    <Box h="320px" overflowY="auto" onScroll={handleScroll}>
                      <VStack gap="12px" align="stretch">
                        {candidates.length === 0 ? (
                          <Text color="neutrals.600" textAlign="center" py="40px">
                            {searchQuery
                              ? 'No candidates match your search criteria'
                              : 'No available candidates'}
                          </Text>
                        ) : (
                          <>
                            {candidates.map((candidate) => (
                              <Card.Root
                                key={candidate.candidate_id}
                                bg="neutrals.50"
                                borderRadius="8px"
                                p="16px"
                                cursor="pointer"
                                transition="all 0.2s ease-in-out"
                              >
                                <Flex justify="space-between" align="center">
                                  <VStack align="flex-start" gap="8px" flex="1">
                                    <Flex gap="12px">
                                      <Text
                                        fontSize="16px"
                                        fontWeight="semibold"
                                        color="neutrals.900"
                                      >
                                        {getCandidateFullName(
                                          candidate.first_name,
                                          candidate.last_name
                                        )}
                                      </Text>{' '}
                                      <Badge
                                        bg="primary.100"
                                        color="primary.900"
                                        px="12px"
                                        py="2px"
                                        borderRadius="12px"
                                        fontSize="12px"
                                        fontWeight="medium"
                                      >
                                        {candidate.unique_id}
                                      </Badge>
                                      {candidate.fit_positions &&
                                        candidate.fit_positions.length > 0 && (
                                          <Badge
                                            bg="neutrals.200"
                                            color="neutrals.700"
                                            px="8px"
                                            py="2px"
                                            borderRadius="4px"
                                            fontSize="12px"
                                          >
                                            {candidate.fit_positions[0]}
                                          </Badge>
                                        )}
                                    </Flex>

                                    <Text fontSize="14px" color="neutrals.700">
                                      {getCandidateLatestWorkExperience(candidate.resume_data)}
                                    </Text>

                                    <Text fontSize="14px" color="neutrals.700">
                                      {candidate.email}
                                    </Text>
                                  </VStack>

                                  <Button
                                    p="8px"
                                    bg="neutrals.0"
                                    color="primary.900"
                                    border="1px solid"
                                    borderColor="primary.900"
                                    borderRadius="full"
                                    _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
                                    loading={addingCandidate === candidate.candidate_id}
                                    onClick={() =>
                                      handleAddExistingCandidate(candidate.candidate_id)
                                    }
                                  >
                                    <Plus size={14} weight="bold" />
                                  </Button>
                                </Flex>
                              </Card.Root>
                            ))}

                            {loadingMore && (
                              <Card.Root p="20px" w="100%">
                                <Flex gap="16px" align="center" justify="center">
                                  <Skeleton w="100%" h="60px" />
                                </Flex>
                              </Card.Root>
                            )}
                          </>
                        )}
                      </VStack>
                    </Box>
                  )}
                </Box>
              </VStack>
            </Tabs.Content>
          </Tabs.Root>

          {activeTab === 'upload' && errorFiles.length > 0 && (
            <Box
              color="error.500"
              fontSize="16px"
              mt="16px"
              p="12px"
              bg="error.50"
              borderRadius="8px"
            >
              Some files failed to upload. You can retry failed uploads individually.
            </Box>
          )}
        </DialogBody>

        <DialogFooter flexShrink={0}>
          <DialogActionTrigger asChild>
            <Button
              w="120px"
              borderColor="primary.900"
              background="background.secondary"
              color="primary.900"
              borderRadius="8px"
              fontSize="16px"
              disabled={
                isSubmitting ||
                (activeTab === 'select' && (loadingCandidates || addingCandidate !== null))
              }
            >
              Close
            </Button>
          </DialogActionTrigger>

          {activeTab === 'upload' ? (
            !canUpload ? (
              <Button
                w="120px"
                background="primary.900"
                color="neutrals.0"
                borderRadius="8px"
                fontSize="16px"
                onClick={onSubmit}
                loading={isSubmitting}
                disabled={files.length === 0 || isSubmitting}
              >
                {isSubmitting ? 'Uploading...' : 'Upload'}
              </Button>
            ) : (
              <DialogActionTrigger asChild>
                <Button
                  w="120px"
                  background="primary.900"
                  color="neutrals.0"
                  borderRadius="8px"
                  fontSize="16px"
                >
                  Done
                </Button>
              </DialogActionTrigger>
            )
          ) : null}
        </DialogFooter>

        <DialogCloseTrigger
          m="12px"
          disabled={
            isSubmitting ||
            (activeTab === 'select' && (loadingCandidates || addingCandidate !== null))
          }
        >
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
